import React, { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { fetchTicketingUsersByLocation, TicketingLocationUser } from "../../../api/ticketApi";

type Props = {
  visible: boolean;
  onClose: () => void;
  /** Selected Ticket Level values used to scope the user lookup. */
  regions?: string[];
  markets?: string[];
  locations?: string[];
  /** Emails already added as stakeholders — shown checked/disabled and excluded from the Attach result. */
  existingEmails?: string[];
  onAttach: (users: TicketingLocationUser[]) => void;
};

type UserGroup = {
  key: string;
  locationName: string;
  locationSapId: string;
  users: TicketingLocationUser[];
};

const SelectStakeholdersModal: React.FC<Props> = ({
  visible,
  onClose,
  regions = [],
  markets = [],
  locations = [],
  existingEmails = [],
  onAttach,
}) => {
  const [users, setUsers] = useState<TicketingLocationUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const regionsKey = regions.join("|");
  const marketsKey = markets.join("|");
  const locationsKey = locations.join("|");

  useEffect(() => {
    if (!visible) return;
    setSearch("");
    setSelectedEmails(new Set());
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const results = await fetchTicketingUsersByLocation({ regions, markets, locations });
        if (cancelled) return;
        setUsers(results);
        const groupKeys = new Set(results.map((u) => `${u.locationName}|${u.locationSapId}`));
        // Multiple locations start collapsed per spec; a single location has no group header.
        setCollapsedGroups(groupKeys.size > 1 ? groupKeys : new Set());
      } catch (err: any) {
        if (!cancelled) {
          setUsers([]);
          setError(
            err?.response?.data?.message || err?.message || "Unable to load users for the selected location(s)."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, regionsKey, marketsKey, locationsKey]);

  const existingEmailSet = useMemo(
    () => new Set(existingEmails.map((e) => e.trim().toLowerCase())),
    [existingEmails]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.gpid || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  const groups = useMemo<UserGroup[]>(() => {
    const map = new Map<string, UserGroup>();
    filteredUsers.forEach((u) => {
      const key = `${u.locationName}|${u.locationSapId}`;
      if (!map.has(key)) {
        map.set(key, { key, locationName: u.locationName, locationSapId: u.locationSapId, users: [] });
      }
      map.get(key)!.users.push(u);
    });
    return Array.from(map.values());
  }, [filteredUsers]);

  const hasMultipleLocations = groups.length > 1;

  const toggleUser = (email: string) => {
    const key = email.trim().toLowerCase();
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleAttach = () => {
    const matched = users.filter((u) => selectedEmails.has((u.email || "").trim().toLowerCase()));
    // A user can appear once per location they have access to — collapse to one row per email.
    const seen = new Set<string>();
    const selected = matched.filter((u) => {
      const key = (u.email || "").trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (!selected.length) return;
    onAttach(selected);
    onClose();
  };

  const renderUserRow = (user: TicketingLocationUser) => {
    const emailKey = (user.email || "").trim().toLowerCase();
    const alreadyAdded = existingEmailSet.has(emailKey);
    const isSelected = alreadyAdded || selectedEmails.has(emailKey);
    return (
      <Pressable
        key={`${user.gpid}-${emailKey}`}
        onPress={() => !alreadyAdded && toggleUser(user.email)}
        disabled={alreadyAdded}
        style={({ pressed }) => [
          s.row,
          isSelected && s.rowSelected,
          pressed && !alreadyAdded ? { opacity: 0.85 } : null,
        ]}
      >
        <MaterialIcons
          name={isSelected ? "check-box" : "check-box-outline-blank"}
          size={20}
          color={alreadyAdded ? "#9aa4b2" : isSelected ? "#006fce" : "#6b7280"}
        />
        <Text style={[s.cellGpid, alreadyAdded && s.cellDisabledText]} numberOfLines={1}>{user.gpid}</Text>
        <Text style={[s.cellName, alreadyAdded && s.cellDisabledText]} numberOfLines={1}>{user.name}</Text>
        <Text style={[s.cellEmail, alreadyAdded && s.cellDisabledText]} numberOfLines={1}>{user.email}</Text>
        <Text style={[s.cellRole, alreadyAdded && s.cellDisabledText]} numberOfLines={1}>{user.role}</Text>
      </Pressable>
    );
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={s.overlay}>
        <View style={s.card}>
          <View style={s.header}>
            <Text style={s.title}>User List</Text>
            <Pressable onPress={onClose} style={s.closeBtn}>
              <MaterialIcons name="close" size={20} color="#3A3A3A" />
            </Pressable>
          </View>

          <View style={s.searchWrap}>
            <MaterialIcons name="search" size={18} color="#9aa4b2" />
            <TextInput
              style={s.searchInput}
              value={search}
              onChangeText={setSearch}
              placeholder="Search by name, email, GPID, or role"
              placeholderTextColor="#9aa4b2"
              autoCapitalize="none"
            />
          </View>

          <View style={s.listHeaderRow}>
            <View style={{ width: 20 }} />
            <Text style={[s.listHeaderText, s.cellGpid]}>GPID</Text>
            <Text style={[s.listHeaderText, s.cellName]}>Name</Text>
            <Text style={[s.listHeaderText, s.cellEmail]}>Email</Text>
            <Text style={[s.listHeaderText, s.cellRole]}>Role</Text>
          </View>

          <ScrollView style={s.body} nestedScrollEnabled keyboardShouldPersistTaps="handled">
            {loading ? (
              <View style={s.centerState}>
                <ActivityIndicator size="small" color="#006fce" />
                <Text style={s.centerStateText}>Loading users…</Text>
              </View>
            ) : error ? (
              <View style={s.centerState}>
                <Text style={[s.centerStateText, { color: "#c12c01" }]}>{error}</Text>
              </View>
            ) : filteredUsers.length === 0 ? (
              <View style={s.centerState}>
                <Text style={s.centerStateText}>No users found for the selected location(s).</Text>
              </View>
            ) : hasMultipleLocations ? (
              groups.map((group) => {
                const isCollapsed = collapsedGroups.has(group.key);
                return (
                  <View key={group.key}>
                    <Pressable onPress={() => toggleGroup(group.key)} style={s.groupHeader}>
                      <MaterialIcons
                        name={isCollapsed ? "keyboard-arrow-right" : "keyboard-arrow-down"}
                        size={18}
                        color="#374151"
                      />
                      <Text style={s.groupHeaderText}>
                        {group.locationName}
                        {group.locationSapId ? ` (SAP: ${group.locationSapId})` : ""}
                      </Text>
                      <Text style={s.groupHeaderCount}>{group.users.length}</Text>
                    </Pressable>
                    {!isCollapsed ? group.users.map(renderUserRow) : null}
                  </View>
                );
              })
            ) : (
              filteredUsers.map(renderUserRow)
            )}
          </ScrollView>

          <View style={s.footer}>
            <Text style={s.selectedCountText}>{selectedEmails.size} selected</Text>
            <Pressable
              onPress={handleAttach}
              disabled={selectedEmails.size === 0}
              style={({ pressed }) => [
                s.attachBtn,
                selectedEmails.size === 0 && s.attachBtnDisabled,
                pressed && selectedEmails.size > 0 ? { opacity: 0.85 } : null,
              ]}
            >
              <Text style={s.attachBtnText}>Attach</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default SelectStakeholdersModal;

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  card: {
    backgroundColor: "#ffffff",
    width: "100%",
    maxWidth: 640,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    display: "flex",
    flexDirection: "column",
    maxHeight: "85%",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  title: {
    fontSize: 17,
    fontWeight: "700",
    color: "#111827",
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
  },
  searchWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginHorizontal: 20,
    marginTop: 14,
    paddingHorizontal: 12,
    height: 38,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    backgroundColor: "#ffffff",
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: "#111827",
  },
  listHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginHorizontal: 20,
    marginTop: 14,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  listHeaderText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#6b7280",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  body: {
    flexGrow: 0,
    paddingHorizontal: 20,
  },
  centerState: {
    paddingVertical: 32,
    alignItems: "center",
    gap: 8,
  },
  centerStateText: {
    fontSize: 13,
    color: "#6b7280",
    textAlign: "center",
  },
  groupHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  groupHeaderText: {
    flex: 1,
    fontSize: 13,
    fontWeight: "700",
    color: "#111827",
  },
  groupHeaderCount: {
    fontSize: 12,
    color: "#6b7280",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderRadius: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#F5F5F5",
  },
  rowSelected: {
    backgroundColor: "#EAF0FB",
  },
  cellGpid: { width: 84, fontSize: 13, color: "#374151" },
  cellName: { flex: 1.2, fontSize: 13, color: "#111827", fontWeight: "600" },
  cellEmail: { flex: 1.6, fontSize: 13, color: "#374151" },
  cellRole: { flex: 1, fontSize: 13, color: "#374151" },
  cellDisabledText: { color: "#9aa4b2" },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  selectedCountText: {
    fontSize: 13,
    color: "#6b7280",
  },
  attachBtn: {
    backgroundColor: "#006fce",
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
  },
  attachBtnDisabled: {
    backgroundColor: "#c7d5e6",
  },
  attachBtnText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#ffffff",
  },
});
